import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuBuilding2, LuFileText, LuSearch } from 'react-icons/lu';
import { repo, type GlobalSearchResult } from '@/services';
import { useDebounce } from '@/hooks/useDebounce';
import { useClickOutside } from '@/hooks/useClickOutside';
import { StatusBadge } from '../ui/StatusBadge';
import { Spinner } from '../ui/Spinner';
import { cn } from '@/lib/cn';

/** Header search across GEM ID, tender/PO/contract numbers, titles and vendors. Ctrl/⌘+K to focus. */
export function GlobalSearch({ className }: { className?: string }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<GlobalSearchResult>({ tenders: [], vendors: [] });
  const [cursor, setCursor] = useState(0);
  const debounced = useDebounce(term, 200);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useClickOutside(wrapRef, () => setOpen(false), open);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (debounced.trim().length < 2) {
      setResults({ tenders: [], vendors: [] });
      return;
    }
    setLoading(true);
    repo.search
      .global(debounced)
      .then((r) => !cancelled && (setResults(r), setCursor(0)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const flat = [
    ...results.tenders.map((t) => ({ kind: 'tender' as const, id: t.id, go: () => navigate(`/tenders/${t.id}`) })),
    ...results.vendors.map((v) => ({ kind: 'vendor' as const, id: v.id, go: () => navigate(`/vendors?vendor=${v.id}`) })),
  ];
  const choose = (i: number) => {
    flat[i]?.go();
    setOpen(false);
    setTerm('');
    inputRef.current?.blur();
  };
  const showPanel = open && term.trim().length >= 2;

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <LuSearch className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(flat.length - 1, c + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
          if (e.key === 'Enter') {
            if (flat.length) choose(cursor);
            else if (term.trim()) { navigate(`/tenders?q=${encodeURIComponent(term.trim())}`); setOpen(false); }
          }
        }}
        placeholder="Search GEM ID, CRFQ, tender, PO, vendor…"
        aria-label="Global search"
        className="h-9 w-full rounded-md border-0 bg-slate-100 pl-8 pr-14 text-sm text-slate-900 placeholder:text-slate-500 focus:bg-white focus:ring-2 focus:ring-brand-600 focus:outline-none"
      />
      <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-slate-300 bg-white px-1.5 text-[10px] font-medium text-slate-500 sm:block">
        Ctrl K
      </kbd>
      {showPanel && (
        <div className="absolute left-0 right-0 top-11 z-40 max-h-[70vh] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl">
          {loading && !flat.length && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-slate-500"><Spinner className="size-4" /> Searching…</div>
          )}
          {!loading && !flat.length && <p className="px-3 py-3 text-sm text-slate-500">No matches for “{term}”.</p>}
          {results.tenders.length > 0 && <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tenders</p>}
          {results.tenders.map((t, i) => (
            <button
              key={t.id}
              onMouseEnter={() => setCursor(i)}
              onClick={() => choose(i)}
              className={cn('flex w-full items-start gap-3 px-3 py-2 text-left', cursor === i && 'bg-brand-50')}
            >
              <LuFileText className="mt-0.5 size-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <p className="gem-id">{t.gemId}{t.tenderNumber && <span className="ml-2 font-sans text-xs font-normal text-slate-500">CRFQ {t.tenderNumber}</span>}</p>
                <p className="truncate text-xs text-slate-600">{t.title}{t.vendorName ? ` · ${t.vendorName}` : ''}</p>
              </div>
              <StatusBadge status={t.status} />
            </button>
          ))}
          {results.vendors.length > 0 && <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Vendors</p>}
          {results.vendors.map((v, j) => {
            const i = results.tenders.length + j;
            return (
              <button
                key={v.id}
                onMouseEnter={() => setCursor(i)}
                onClick={() => choose(i)}
                className={cn('flex w-full items-center gap-3 px-3 py-2 text-left', cursor === i && 'bg-brand-50')}
              >
                <LuBuilding2 className="size-4 shrink-0 text-slate-400" />
                <span className="flex-1 truncate text-sm text-slate-800">{v.name}</span>
                <span className="font-mono text-xs text-slate-500">{v.vendorCode}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
